import type { createApp } from 'vue';
import { resetChubbLegacyConfig } from '@/config/chubbLegacy';
import { setMountSessionToken } from '@/utils/sessionToken';
import { initWidgetAnalytics } from '@/utils/widget-analytics';
import { applyMountConfig, applyUpdateConfig, resolveAgentAuth } from './hostConfig';
import { widgetInstances } from './registry';
import { applyResponsiveStyles } from './responsive';
import type { WidgetInstance, WidgetOptions } from './types';
import { buildWidgetApp } from './vueApp';

type VueApp = ReturnType<typeof createApp>;

function disposeExisting(container: HTMLElement): void {
  const existing = widgetInstances.get(container);
  if (existing) {
    existing.app.unmount();
    widgetInstances.delete(container);
  }
}

function scheduleLoadedEvent(container: HTMLElement, options: WidgetOptions, agentId?: string): void {
  if (options.emitEvents === false) return;
  setTimeout(() => {
    const event = new CustomEvent('cotizador:loaded', {
      detail: { container, options, agentId: agentId || options.agentId },
    });
    document.body.dispatchEvent(event);
  }, 100);
}

function createInstance(
  container: HTMLElement,
  app: VueApp,
  options: WidgetOptions,
  resizeHandler: () => void,
): WidgetInstance {
  return {
    unmount: () => {
      window.removeEventListener('resize', resizeHandler);
      app.unmount();
      widgetInstances.delete(container);
      setMountSessionToken(null);
      resetChubbLegacyConfig();

      if (options.emitEvents !== false) {
        const event = new CustomEvent('cotizador:unmounted', { detail: { container } });
        document.body.dispatchEvent(event);
      }
    },
    updateConfig: async (newOptions: WidgetOptions) => {
      await applyUpdateConfig(newOptions, options);
      // Re-apply styles if the responsive options change
      if (newOptions.minHeight || newOptions.maxHeight) {
        applyResponsiveStyles(container, { ...options, ...newOptions });
      }
    },
  };
}

export async function mountWidget(container: HTMLElement, options: WidgetOptions = {}): Promise<WidgetInstance> {
  initWidgetAnalytics();
  disposeExisting(container);
  applyMountConfig(options);

  const { agentId, canEmit } = await resolveAgentAuth(options);
  const app = buildWidgetApp(options, canEmit);
  app.mount(container);

  scheduleLoadedEvent(container, options, agentId);
  applyResponsiveStyles(container, options);

  const resizeHandler = () => applyResponsiveStyles(container, options);
  window.addEventListener('resize', resizeHandler);

  const instance = createInstance(container, app, options, resizeHandler);
  widgetInstances.set(container, { app, instance });
  return instance;
}
